import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { toast } from 'sonner';
import { useDrawsUpdated } from '../hooks/useDrawsUpdated';
import useConsensusGroups from '../hooks/useConsensusGroups';
import { GROUP_STYLE, GroupLegend, ConsensusRefLink, QualityBadge, applyConsensusFilter } from '../components/ConsensusBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BALL_LABELS = { ball_1: 'B1', ball_2: 'B2', ball_3: 'B3', ball_4: 'B4', ball_5: 'B5' };
const WINDOW_DESCRIPTIONS = {
  W3: 'Per-Ball Difference',
  W14: 'Sum Range',
  W24: 'Ball Position (1-1-1-1-1)',
  W7: 'HML Tiers',
  W4: 'True/False/Zero',
  W8: 'Before/After',
  W5: 'Per-Ball Frequency',
  W6: 'Monthly Frequency',
  W11: 'Cross Analysis',
  W13: 'Class Mix',
  W17: 'Comparison to Previous Draws',
  W18: 'Gap Analysis',
  W19: 'Position Freq',
  W2: 'Odd/Even',
  W21: 'Gap By Position',
  W22: 'Zone Coverage',
  W27: 'Jackpot Winners',
  W30: 'Sequence',
  Date: 'Date Digit',
};

export default function MultiWindowProjection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logging, setLogging] = useState(false);
  const [tracker, setTracker] = useState(null);
  const [showWeights, setShowWeights] = useState(false);
  const [weightsDraft, setWeightsDraft] = useState({});
  const [consensusFilter, setConsensusFilter] = useState("MAX_1_RED"); // default: MAX_1_RED
  const consensus = useConsensusGroups();

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [ensembleRes, trackerRes] = await Promise.all([
        axios.get(`${API}/multi-window/ensemble`),
        axios.get(`${API}/multi-window/hit-tracker`),
      ]);
      setData(ensembleRes.data);
      setTracker(trackerRes.data);
      setWeightsDraft(ensembleRes.data.weights || {});
    } catch (e) {
      toast.error('Failed to load Multi-Window Projection');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useDrawsUpdated(fetchAll);

  const saveWeights = async () => {
    try {
      const res = await axios.post(`${API}/multi-window/weights`, { weights: weightsDraft });
      setData((prev) => prev ? { ...prev, weights: res.data.weights } : prev);
      toast.success('Weights saved — recomputing...');
      await fetchAll();
    } catch (e) {
      toast.error('Failed to save weights');
    }
  };

  const resetWeights = async () => {
    try {
      await axios.post(`${API}/multi-window/weights/reset`);
      toast.success('Weights reset to defaults');
      await fetchAll();
    } catch (e) {
      toast.error('Failed to reset');
    }
  };

  const autoTune = async () => {
    try {
      const res = await axios.post(`${API}/multi-window/auto-tune-weights`);
      toast.success(`ML Auto-tune applied (${res.data.changes?.length || 0} weights adjusted)`);
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Auto-tune failed');
    }
  };

  const snapshot = async () => {
    try {
      setLogging(true);
      const res = await axios.post(`${API}/multi-window/log-prediction`);
      toast.success(`Snapshot logged for draw #${res.data.target_draw_number}`);
      await fetchAll();
    } catch (e) {
      toast.error('Failed to log snapshot');
    } finally {
      setLogging(false);
    }
  };

  const exportCompareExcel = async () => {
    try {
      toast.info('Generating comparison workbook...');
      const [combinedRes, consensusRes] = await Promise.all([
        axios.get(`${API}/analysis/combined-predictions`).catch(() => ({ data: null })),
        axios.get(`${API}/analysis/summary-dashboard`).catch(() => ({ data: null })),
      ]);
      const combined = combinedRes.data || {};
      const consensus = consensusRes.data || {};
      const wb = XLSX.utils.book_new();

      // Sheet 1 — Full Side-by-Side Comparison (matches the table shown in chat)
      const overview = [
        ['Prediction Engines — Side-by-Side Comparison'],
        ['Generated at', new Date().toISOString()],
        [],
        ['Feature', 'Combined Predictions', 'Multi-Window Projection', 'Consensus Predictions'],
        ['Purpose', 'Per-ball prediction grid from each window separately', 'Full ensemble engine with auto-constraints', 'Rule-scored validated tickets'],
        ['Input', 'Per-window top picks (already computed)', 'Probability vectors from every window, weighted', 'Summary engines + combo validators'],
        ['Aggregation', 'Side-by-side display (not combined)', 'Weighted sum of vectors → consensus', 'Rule-based voting + filters'],
        ['Constraints used', 'None (informational only)', 'Auto-derived from W3: sign, bands, Σdiff range, per-ball bounds', 'Manual C1-C5 user constraints'],
        ['Output per ball', "Each window's top 5-10 picks", 'Single ensemble top-10 + probability %', 'Winner-rules-validated numbers'],
        ['Combinations', 'Not generated', 'Top-10 main-ball combos + Top-10 LS pairs + Top-5 full 7-ball tickets', "Top validated combos matching user's C1-C5"],
        ['Lucky Stars', 'Shown separately per window', 'Full ensemble (LS1-LS2 top-12 + pairs)', 'In combos'],
        ['Weights', 'N/A — pure display', 'Configurable sliders (W3=22%, W24=0, etc.)', 'Fixed rule weights'],
        ['Learning', 'No', 'Hit tracker + ML auto-tune after 20 draws', 'No'],
        ['User input needed', 'No', 'No (auto-constraints)', 'Yes (C1-C5 must be set)'],
        ['Best used for', '"Which window picks what?"', '"Give me the most likely data-driven full ticket"', '"Which combos pass MY manual rules?"'],
        [],
        ['Analogy'],
        ['Combined Predictions', 'Here are 14 weather forecasters\' individual predictions.'],
        ['Multi-Window Projection', "Here is the AVERAGE forecast weighted by each forecaster's skill, with confidence intervals and ML learning."],
        ['Consensus Predictions', 'Here are the forecasts that pass MY personal filter (rain boots required + no sunshine days).'],
        [],
        ['Recommendation on when to use each'],
        ['Situation', 'Engine to use'],
        ['You want to explore what each window says', 'Combined Predictions'],
        ['You want the single best data-driven prediction without setting rules manually', 'Multi-Window Projection'],
        ['You have strong personal intuition and want to enforce YOUR rules', 'Consensus Predictions (with C1-C5)'],
        [],
        ['Workflow suggestion'],
        ['1. Start every draw cycle by looking at Multi-Window Projection → check the top-5 7-ball tickets.'],
        ['2. If they look reasonable, click "Log Snapshot" so hits can be scored later.'],
        ['3. Optionally cross-check against Consensus Predictions if any C1-C5 intuition applies.'],
        ['4. After 20 draws, Multi-Window\'s ML auto-tune will likely be most reliable (it learns from real hits).'],
      ];
      const wsOv = XLSX.utils.aoa_to_sheet(overview);
      wsOv['!cols'] = [{ wch: 28 }, { wch: 60 }, { wch: 60 }, { wch: 60 }];
      // Merge the title cell across 4 columns
      wsOv['!merges'] = wsOv['!merges'] || [];
      wsOv['!merges'].push({ s: { r: 0, c: 0 }, e: { r: 0, c: 3 } });
      XLSX.utils.book_append_sheet(wb, wsOv, 'Side-by-Side Comparison');

      // Sheet 2 — Top Picks per Ball (side-by-side)
      const hdr = ['Ball', 'Multi-Window (Top 10, prob%)', 'Combined (top per window)', 'Consensus (summary top)'];
      const mwBalls = data.balls || {};
      const mwLS = data.lucky_stars || {};
      const combinedBalls = (combined.balls || combined.predictions || {});
      const consensusBalls = (consensus.balls || {});
      const rows = [hdr];
      const ballLabels = [['ball_1','B1'],['ball_2','B2'],['ball_3','B3'],['ball_4','B4'],['ball_5','B5']];
      for (const [bk, bl] of ballLabels) {
        const mw = (mwBalls[bk]?.top10 || []).map(p => `${p.number} (${p.probability}%)`).join(', ');
        const comb = JSON.stringify(combinedBalls[bk]?.top_picks || combinedBalls[bk]?.combined || combinedBalls[bk] || '-').slice(0, 400);
        const cons = JSON.stringify(consensusBalls[bk]?.top_picks || consensusBalls[bk]?.top_numbers || consensusBalls[bk] || '-').slice(0, 400);
        rows.push([bl, mw, comb, cons]);
      }
      for (const lsk of ['lucky_star_1', 'lucky_star_2']) {
        const mwTop = (mwLS[lsk]?.top12 || []).map(p => `${p.number} (${p.probability}%)`).join(', ');
        rows.push([lsk === 'lucky_star_1' ? 'LS1' : 'LS2', mwTop, '-', '-']);
      }
      const wsBalls = XLSX.utils.aoa_to_sheet(rows);
      wsBalls['!cols'] = [{ wch: 8 }, { wch: 72 }, { wch: 60 }, { wch: 60 }];
      XLSX.utils.book_append_sheet(wb, wsBalls, 'Per-Ball Top Picks');

      // Sheet 3 — Multi-Window Top-10 Main-Ball Combos
      const mwCombos = data.top_combinations || [];
      const comboRows = [['#', 'B1', 'B2', 'B3', 'B4', 'B5', 'Sum', 'Σ Diff', 'Joint Score', 'Per-Ball Probs']];
      mwCombos.forEach((c, i) => {
        comboRows.push([i + 1, ...c.numbers, c.sum, c.total_of_diff, c.joint_score, (c.per_ball_prob || []).join(' × ')]);
      });
      const wsCombos = XLSX.utils.aoa_to_sheet(comboRows);
      XLSX.utils.book_append_sheet(wb, wsCombos, 'MW Top-10 Combos');

      // Sheet 4 — MW Top-10 LS Pairs
      const lsRows = [['#', 'LS1', 'LS2', 'LS Sum', 'Joint Score', 'Per-LS Probs']];
      (data.top_ls_pairs || []).forEach((p, i) => {
        lsRows.push([i + 1, p.ls1, p.ls2, p.sum, p.joint_score, (p.per_ls_prob || []).join(' × ')]);
      });
      const wsLS = XLSX.utils.aoa_to_sheet(lsRows);
      XLSX.utils.book_append_sheet(wb, wsLS, 'MW LS Pairs');

      // Sheet 5 — MW Full 7-Ball Tickets
      const tkHdr = ['#', 'B1', 'B2', 'B3', 'B4', 'B5', 'LS1', 'LS2', 'Sum', 'Σ Diff', 'LS Sum'];
      const tkRows = [tkHdr];
      (mwCombos.slice(0, 10)).forEach((c, i) => {
        const lsp = (data.top_ls_pairs || [])[i] || (data.top_ls_pairs || [])[0] || { ls1: '-', ls2: '-', sum: '-' };
        tkRows.push([i + 1, ...c.numbers, lsp.ls1, lsp.ls2, c.sum, c.total_of_diff, lsp.sum]);
      });
      const wsTk = XLSX.utils.aoa_to_sheet(tkRows);
      XLSX.utils.book_append_sheet(wb, wsTk, 'MW 7-Ball Tickets');

      // Sheet 6 — Consensus top combinations (if available)
      const cTopCombos = consensus.top_combinations || consensus.predictions || [];
      const cRows = [['#', 'B1', 'B2', 'B3', 'B4', 'B5', 'Sum', 'Score', 'Rules/Engines']];
      cTopCombos.slice(0, 50).forEach((c, i) => {
        const nums = c.numbers || c.balls || [];
        cRows.push([i + 1, ...nums, (c.sum ?? ''), (c.score ?? c.total_score ?? ''), JSON.stringify(c.engines || c.rules || c.sources || '-').slice(0, 200)]);
      });
      const wsC = XLSX.utils.aoa_to_sheet(cRows);
      XLSX.utils.book_append_sheet(wb, wsC, 'Consensus Combos');

      // Sheet 7 — Auto-derived constraints (MW)
      const ac = data.auto_constraints || {};
      const acRows = [
        ['Auto-Derived Constraints (Multi-Window Projection)'],
        [],
        ['Sign Pattern', (ac.sign_pattern || []).join(' ')],
        ['Dominant Bands', (ac.dominant_bands || []).join(' ')],
        ['Scenarios Considered', (ac.scenarios_considered || []).map((r, i) => `${r} (${ac.scenarios_pct?.[i]}%)`).join(', ')],
        ['Predicted Σdiff Range', `${ac.predicted_total_range?.low} to ${ac.predicted_total_range?.high}`],
        ['Latest Total', ac.latest_total],
        [],
        ['Ball', 'Lower Bound', 'Upper Bound'],
        ...(ac.per_ball_bounds || []).map(b => [b.ball, b.low, b.high]),
      ];
      const wsAC = XLSX.utils.aoa_to_sheet(acRows);
      wsAC['!cols'] = [{ wch: 26 }, { wch: 16 }, { wch: 16 }];
      XLSX.utils.book_append_sheet(wb, wsAC, 'MW Auto-Constraints');

      const fileName = `prediction_compare_${new Date().toISOString().slice(0, 10)}.xlsx`;
      XLSX.writeFile(wb, fileName);
      toast.success(`Exported ${fileName}`);
    } catch (e) {
      console.error(e);
      toast.error('Export failed');
    }
  };

  if (loading) return <div className="py-20 text-center text-gray-500">Computing ensemble...</div>;
  if (!data) return <div className="py-20 text-center text-red-500">No data available</div>;

  const { balls = {}, lucky_stars = {}, top_ls_pairs = [], auto_constraints = {}, top_combinations = [], w3_predictions = {}, weights = {} } = data;
  const predAvg = w3_predictions.pred_avg || [];
  const predDom = w3_predictions.pred_dom || [];
  const summary = tracker?.summary || {};

  // Centralised filter+sort (Premium → Solid → Caution, then by consensus boost if RERANK)
  const combosWithBalls = top_combinations.map(c => ({ ...c, balls: c.numbers }));
  const displayedCombinations = applyConsensusFilter(combosWithBalls, consensusFilter, consensus);

  return (
    <div className="space-y-4">
      {/* Header banner */}
      <div className="bg-gradient-to-r from-fuchsia-700 via-purple-700 to-indigo-700 text-white rounded-lg px-5 py-4">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-bold text-xl">Multi-Window Projection — Ensemble</h2>
            <p className="text-xs text-white/80 mt-1">
              Consensus prediction = weighted aggregation of every analysis window's probability vector.
              Auto-derived constraints from Window 3 conditional bucket analysis, no manual C1-C5 override.
              Top-10 ball combinations ranked by joint probability.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={exportCompareExcel} data-testid="mw-export-compare-btn" className="text-xs bg-emerald-500 hover:bg-emerald-600 text-white font-bold px-3 py-1.5 rounded">
              ⬇ Export 3-Engine Comparison (Excel)
            </button>
            <button onClick={snapshot} disabled={logging} data-testid="mw-snapshot-btn" className="text-xs bg-amber-500 hover:bg-amber-600 text-white font-bold px-3 py-1.5 rounded disabled:opacity-50">
              {logging ? 'Logging...' : 'Log Snapshot for Hit Tracker'}
            </button>
            <button onClick={() => setShowWeights(!showWeights)} data-testid="mw-toggle-weights" className="text-xs bg-white/20 hover:bg-white/30 text-white font-bold px-3 py-1.5 rounded">
              {showWeights ? 'Hide' : 'Show'} Weights
            </button>
          </div>
        </div>
      </div>

      {/* Consensus Filter / Re-rank control */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-700 text-white rounded-lg px-4 py-3 flex flex-wrap items-center gap-3" data-testid="mw-consensus-filter">
        <span className="text-xs font-bold">🚦 Consensus Filter:</span>
        {[
          ["OFF", "Off"],
          ["MAX_1_RED", "Max 1 RED ⭐"],
          ["NO_RED", "No RED"],
          ["ONLY_GREEN", "Only GREEN"],
          ["RERANK", "Re-rank"],
        ].map(([k, l]) => (
          <button key={k} onClick={() => setConsensusFilter(k)} className={`text-xs px-2.5 py-1 rounded-full font-bold border ${consensusFilter === k ? 'bg-white text-slate-800 border-white' : 'bg-transparent text-white border-white/30 hover:border-white'}`} data-testid={`mw-filter-${k}`}>{l}</button>
        ))}
        <span className="text-[10px] text-white/70 ml-2">Default: Max 1 RED (90% historical survival). Always sorted Premium → Solid → Caution. Showing <span className="font-bold font-mono">{displayedCombinations.length}</span> of {top_combinations.length} combos.</span>
        <div className="ml-auto flex items-center gap-3">
          <GroupLegend groups={consensus.groups} />
          <ConsensusRefLink className="text-white hover:text-amber-300" />
        </div>
      </div>

      {/* Hit tracker summary */}
      {summary.total_draws_tracked !== undefined && (
        <div className="bg-white border rounded-lg p-4" data-testid="mw-hit-tracker-summary">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <h4 className="font-bold text-gray-900">Hit Tracker Summary</h4>
            {summary.ready_for_ml && (
              <button onClick={autoTune} data-testid="mw-auto-tune-btn" className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-3 py-1 rounded">
                ↻ Run ML Auto-Tune Weights
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-center">
            <Stat label="Draws Tracked" value={summary.total_draws_tracked} />
            <Stat label="Pred Avg (exact)" value={`${summary.pred_avg_exact_pct ?? 0}%`} />
            <Stat label="Pred Avg (±3)" value={`${summary.pred_avg_within_3_pct ?? 0}%`} />
            <Stat label="Pred Avg (±5)" value={`${summary.pred_avg_within_5_pct ?? 0}%`} />
            <Stat label="Top10 Inclusion" value={`${summary.top10_inclusion_pct ?? 0}%`} />
          </div>
          <div className="mt-2 text-xs text-gray-500">
            {summary.ready_for_ml ? (
              <span className="text-emerald-700 font-bold">✓ Ready for ML auto-tune ({summary.total_draws_tracked} draws ≥ 20 threshold).</span>
            ) : (
              <span>ML auto-tune activates after 20 scored draws (currently {summary.total_draws_tracked}). Use "Log Snapshot" before each real draw so hits can be scored.</span>
            )}
          </div>
        </div>
      )}

      {/* Auto-derived constraints */}
      <div className="bg-gradient-to-r from-amber-100 to-orange-100 border border-amber-300 rounded-lg p-4" data-testid="mw-auto-constraints">
        <h4 className="font-bold text-amber-900 mb-2">Auto-Derived Constraints (no manual input)</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
          <div>
            <div className="text-amber-800 font-bold mb-1">Sign Pattern (Scenario #1)</div>
            <div className="text-2xl font-mono font-bold text-amber-900 tracking-widest">{(auto_constraints.sign_pattern || []).join(' ')}</div>
          </div>
          <div>
            <div className="text-amber-800 font-bold mb-1">Dominant Band Pattern</div>
            <div className="text-2xl font-mono font-bold text-amber-900 tracking-widest">{(auto_constraints.dominant_bands || []).join(' ')}</div>
          </div>
          <div>
            <div className="text-amber-800 font-bold mb-1">Predicted Total-of-Diff Range</div>
            <div className="text-lg font-mono font-bold text-amber-900">{auto_constraints.predicted_total_range?.low} to {auto_constraints.predicted_total_range?.high}</div>
            <div className="text-[10px] text-amber-700">Latest Total: {auto_constraints.latest_total > 0 ? `+${auto_constraints.latest_total}` : auto_constraints.latest_total} | Scenarios: {(auto_constraints.scenarios_considered || []).map((r, i) => `${r} (${auto_constraints.scenarios_pct?.[i]}%)`).join(', ')}</div>
          </div>
        </div>
        <div className="mt-3 grid grid-cols-5 gap-2">
          {(auto_constraints.per_ball_bounds || []).map(b => (
            <div key={b.ball} className="bg-white rounded border border-amber-300 p-2 text-center">
              <div className="text-[10px] font-bold text-amber-700">{b.ball} bounds</div>
              <div className="font-mono font-bold text-amber-900">{b.low}–{b.high}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Window 3 Predictions — three formats */}
      <div className="bg-white border rounded-lg overflow-hidden" data-testid="mw-w3-predictions">
        <div className="bg-indigo-700 text-white px-4 py-3">
          <h4 className="font-bold">Window 3 — Three Prediction Formats (Scenario #1)</h4>
          <p className="text-[10px] text-white/70">Pred (avg) = single best guess | Pred (dom band) = conditional-on-dominant-band | Full range = worst-to-best bounds</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-3 py-2 text-left">Ball</th>
                <th className="px-3 py-2 text-center">Last</th>
                <th className="px-3 py-2 text-center bg-amber-50">Pred (avg)</th>
                <th className="px-3 py-2 text-center bg-violet-50">Dom Pred (band)</th>
                <th className="px-3 py-2 text-center bg-gray-50">Full Range (ensemble bounds)</th>
                <th className="px-3 py-2 text-center">Dom Band</th>
              </tr>
            </thead>
            <tbody>
              {predAvg.map((p, i) => {
                const domP = predDom[i];
                const bound = auto_constraints.per_ball_bounds?.[i];
                return (
                  <tr key={p.ball} className="border-b hover:bg-gray-50">
                    <td className="px-3 py-2 font-bold font-mono text-[#002FA7]">{p.ball}</td>
                    <td className="px-3 py-2 text-center font-mono">{p.last}</td>
                    <td className="px-3 py-2 text-center font-mono font-bold text-amber-800 text-base">{p.predicted}</td>
                    <td className="px-3 py-2 text-center font-mono font-bold text-violet-800 text-base">{domP?.predicted}</td>
                    <td className="px-3 py-2 text-center font-mono text-gray-700">{bound?.low}–{bound?.high}</td>
                    <td className="px-3 py-2 text-center">
                      <span className="inline-flex items-center gap-1">
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-violet-600 text-white text-[10px] font-bold">{p.dominant_band}</span>
                        <span className="text-xs text-gray-500">{p.dominant_band_pct}%</span>
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Per-Ball Top 10 Ensemble Predictions */}
      <div className="bg-white border rounded-lg overflow-hidden" data-testid="mw-per-ball-top10">
        <div className="bg-gradient-to-r from-indigo-700 to-blue-700 text-white px-4 py-3">
          <h4 className="font-bold">Per-Ball Ensemble Top 10 (weighted across all windows)</h4>
          <p className="text-[10px] text-white/70">5 main balls + 2 Lucky Stars. Each ball's candidates with ensemble probability. Used to generate the combinations below.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-0 border-b">
          {Object.keys(BALL_LABELS).map((bk) => {
            const top10 = balls[bk]?.top10 || [];
            return (
              <div key={bk} className="border-r last:border-r-0">
                <div className="bg-gray-800 text-white px-3 py-2 text-center font-bold">{BALL_LABELS[bk]}</div>
                <table className="w-full text-xs">
                  <tbody>
                    {top10.map((p, i) => {
                      const g = consensus.groupOf(p.number);
                      const gs = GROUP_STYLE[g];
                      return (
                      <tr key={p.number} className={`border-b ${i === 0 ? 'bg-emerald-50 font-bold' : ''}`}>
                        <td className="px-2 py-1 text-gray-400 font-mono text-center w-6">{i + 1}</td>
                        <td className="px-2 py-1 text-center">
                          <span className={`inline-flex w-7 h-7 rounded-full items-center justify-center font-mono font-bold text-[#002FA7] text-[11px] ring-2 ring-offset-1 ${gs.ring} bg-white`} title={`${g} · Consensus ${consensus.scoreOf(p.number) >= 0 ? '+' : ''}${consensus.scoreOf(p.number)}`}>{p.number}</span>
                        </td>
                        <td className="px-2 py-1 text-right font-mono text-gray-600">{p.probability}%</td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
        {/* Lucky Stars row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
          {['lucky_star_1', 'lucky_star_2'].map((lsk, li) => {
            const top12 = (lucky_stars[lsk] || {}).top12 || [];
            return (
              <div key={lsk} className="border-r last:border-r-0">
                <div className="bg-yellow-500 text-white px-3 py-2 text-center font-bold">LS{li + 1} (1-12)</div>
                <div className="grid grid-cols-6 gap-0">
                  {top12.map((p, i) => (
                    <div key={p.number} className={`border border-gray-100 px-2 py-2 text-center ${i === 0 ? 'bg-yellow-50 font-bold' : ''}`}>
                      <div className="font-mono font-bold text-[#002FA7] text-base">{p.number}</div>
                      <div className="text-[10px] text-gray-500 font-mono">{p.probability}%</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Top 10 Joint Combinations */}
      <div className="bg-white border rounded-lg overflow-hidden" data-testid="mw-top10-combos">
        <div className="bg-gradient-to-r from-emerald-700 to-teal-700 text-white px-4 py-3">
          <h4 className="font-bold">Top 10 Predicted Combinations (joint probability, ascending, auto-filtered)</h4>
          <p className="text-[10px] text-white/70">
            Filtered by: (1) per-ball auto-bounds, (2) ascending B1&lt;B2&lt;B3&lt;B4&lt;B5, (3) distinct numbers,
            (4) <span className="font-bold">Σ Diff within predicted total-of-diff range ({auto_constraints.predicted_total_range?.low} to {auto_constraints.predicted_total_range?.high})</span> — same range used in Window 3 → 2026 Monthly Diff Matrix.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-3 py-2 text-center text-xs font-bold">#</th>
                <th className="px-3 py-2 text-center text-xs font-bold">B1</th>
                <th className="px-3 py-2 text-center text-xs font-bold">B2</th>
                <th className="px-3 py-2 text-center text-xs font-bold">B3</th>
                <th className="px-3 py-2 text-center text-xs font-bold">B4</th>
                <th className="px-3 py-2 text-center text-xs font-bold">B5</th>
                <th className="px-3 py-2 text-center text-xs font-bold bg-amber-50">Sum</th>
                <th className="px-3 py-2 text-center text-xs font-bold bg-orange-50">Σ Diff</th>
                <th className="px-3 py-2 text-center text-xs font-bold bg-emerald-50">Joint Score</th>
                <th className="px-3 py-2 text-center text-xs font-bold bg-slate-100">G-Y-R Mix</th>
                <th className="px-3 py-2 text-center text-xs font-bold">Per-Ball Probs</th>
              </tr>
            </thead>
            <tbody>
              {displayedCombinations.map((c, i) => (
                <tr key={i} className={`border-b hover:bg-emerald-50/50 ${i === 0 ? 'bg-emerald-100 font-bold' : i < 3 ? 'bg-emerald-50/40' : ''}`} data-testid={`mw-combo-row-${i}`}>
                  <td className="px-3 py-2 text-center text-gray-400 font-mono">
                    <div>{i + 1}</div>
                    <QualityBadge tier={c._quality.tier} size="xs" />
                  </td>
                  {c.numbers.map((n, j) => {
                    const g = consensus.groupOf(n);
                    const gs = GROUP_STYLE[g];
                    return (
                      <td key={j} className="px-3 py-2 text-center">
                        <span className={`inline-flex w-9 h-9 rounded-full items-center justify-center font-mono font-bold text-[#002FA7] ring-2 ring-offset-1 ${gs.ring} bg-white`} title={`${g} · Consensus ${consensus.scoreOf(n) >= 0 ? '+' : ''}${consensus.scoreOf(n)}`}>{n}</span>
                      </td>
                    );
                  })}
                  <td className="px-3 py-2 text-center font-mono font-bold text-amber-800">{c.sum}</td>
                  <td className={`px-3 py-2 text-center font-mono font-bold ${c.total_of_diff > 0 ? 'text-emerald-700' : c.total_of_diff < 0 ? 'text-red-600' : 'text-gray-500'}`}>{c.total_of_diff > 0 ? `+${c.total_of_diff}` : c.total_of_diff}</td>
                  <td className="px-3 py-2 text-center font-mono font-bold text-emerald-700">{c.joint_score}{consensusFilter === 'RERANK' && <span className="block text-[9px] text-slate-500 font-normal">+{c._quality.boost.toFixed(1)} bst</span>}</td>
                  <td className="px-3 py-2 text-center">
                    <div className="flex justify-center gap-1">
                      {["GREEN","YELLOW","RED"].map(gk => (
                        <span key={gk} className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${GROUP_STYLE[gk].chip}`}>{gk[0]}:{c._quality.mix[gk]}</span>
                      ))}
                      <span className="text-[10px] font-mono font-bold text-slate-700 ml-1">Σ {c._quality.score >= 0 ? `+${c._quality.score}` : c._quality.score}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-center text-[10px] font-mono text-gray-500">{c.per_ball_prob.join(' × ')}</td>
                </tr>
              ))}
              {displayedCombinations.length === 0 && (
                <tr><td colSpan={11} className="px-3 py-4 text-center text-gray-400 text-xs">No combos match the Consensus Filter. Try "No RED" or "Off".</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top 10 Lucky Star Pairs */}
      <div className="bg-white border rounded-lg overflow-hidden" data-testid="mw-top10-ls-pairs">
        <div className="bg-gradient-to-r from-yellow-600 to-amber-600 text-white px-4 py-3">
          <h4 className="font-bold">Top 10 Lucky-Star Pairs (LS1 &lt; LS2, 1-12)</h4>
          <p className="text-[10px] text-white/80">Joint probability across LS1/LS2 ensemble vectors. Combine these with the top main-ball combos above for full 7-ball predictions.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-3 py-2 text-center text-xs font-bold">#</th>
                <th className="px-3 py-2 text-center text-xs font-bold">LS1</th>
                <th className="px-3 py-2 text-center text-xs font-bold">LS2</th>
                <th className="px-3 py-2 text-center text-xs font-bold bg-amber-50">LS Sum</th>
                <th className="px-3 py-2 text-center text-xs font-bold bg-emerald-50">Joint Score</th>
                <th className="px-3 py-2 text-center text-xs font-bold">Per-LS Probs</th>
              </tr>
            </thead>
            <tbody>
              {top_ls_pairs.map((p, i) => (
                <tr key={i} className={`border-b hover:bg-yellow-50/50 ${i === 0 ? 'bg-yellow-100 font-bold' : i < 3 ? 'bg-yellow-50/40' : ''}`} data-testid={`mw-ls-pair-${i}`}>
                  <td className="px-3 py-2 text-center text-gray-400 font-mono">{i + 1}</td>
                  <td className="px-3 py-2 text-center font-mono font-bold text-[#002FA7] text-lg">{p.ls1}</td>
                  <td className="px-3 py-2 text-center font-mono font-bold text-[#002FA7] text-lg">{p.ls2}</td>
                  <td className="px-3 py-2 text-center font-mono font-bold text-amber-800">{p.sum}</td>
                  <td className="px-3 py-2 text-center font-mono font-bold text-emerald-700">{p.joint_score}</td>
                  <td className="px-3 py-2 text-center text-[10px] font-mono text-gray-500">{p.per_ls_prob?.join(' × ')}</td>
                </tr>
              ))}
              {top_ls_pairs.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-4 text-center text-gray-400 text-xs">No LS pairs available yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Full 7-ball quick picks */}
      {top_combinations.length > 0 && top_ls_pairs.length > 0 && (
        <div className="bg-gradient-to-r from-purple-700 to-pink-700 text-white rounded-lg p-4" data-testid="mw-full-ticket">
          <h4 className="font-bold mb-3">🎟️ Top 5 Full 7-Ball Tickets (Main combo + LS pair)</h4>
          <div className="space-y-2">
            {top_combinations.slice(0, 5).map((c, i) => {
              const lsp = top_ls_pairs[i] || top_ls_pairs[0];
              return (
                <div key={i} className="bg-white/15 backdrop-blur rounded-lg p-3 flex items-center justify-between flex-wrap gap-2 border border-white/20">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-white/70 font-bold text-sm">#{i + 1}</span>
                    <div className="flex gap-1.5">
                      {c.numbers.map((n) => (
                        <span key={n} className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white text-[#002FA7] font-bold font-mono text-sm">{n}</span>
                      ))}
                    </div>
                    <span className="text-white/50">+</span>
                    <div className="flex gap-1.5">
                      <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-yellow-300 text-gray-800 font-bold font-mono text-sm">{lsp.ls1}</span>
                      <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-yellow-300 text-gray-800 font-bold font-mono text-sm">{lsp.ls2}</span>
                    </div>
                  </div>
                  <div className="text-[10px] text-white/70 font-mono">
                    Sum={c.sum} | Σdiff={c.total_of_diff > 0 ? '+' : ''}{c.total_of_diff} | LS Sum={lsp.sum}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Weight Sliders Panel */}
      {showWeights && (
        <div className="bg-white border rounded-lg p-4" data-testid="mw-weights-panel">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <h4 className="font-bold text-gray-900">Window Weights (manual tuning)</h4>
            <div className="flex gap-2">
              <button onClick={resetWeights} className="text-xs bg-gray-200 hover:bg-gray-300 px-3 py-1 rounded" data-testid="mw-reset-btn">Reset to Defaults</button>
              <button onClick={saveWeights} className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-3 py-1 rounded" data-testid="mw-save-btn">Save & Apply</button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
            {Object.entries(weights).map(([wkey, wval]) => (
              <div key={wkey} className="flex items-center gap-2 py-1">
                <div className="w-28 flex-shrink-0">
                  <div className="text-xs font-bold text-gray-800">{wkey}</div>
                  <div className="text-[10px] text-gray-500">{WINDOW_DESCRIPTIONS[wkey] || ''}</div>
                </div>
                <input
                  type="range" min={0} max={35} step={0.5}
                  value={weightsDraft[wkey] ?? wval}
                  onChange={(e) => setWeightsDraft(prev => ({ ...prev, [wkey]: parseFloat(e.target.value) }))}
                  className="flex-1"
                  data-testid={`mw-slider-${wkey}`}
                />
                <div className="w-12 text-right font-mono font-bold text-sm text-[#002FA7]">{(weightsDraft[wkey] ?? wval).toFixed(1)}%</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Hit Tracker History */}
      {tracker?.items && tracker.items.length > 0 && (
        <div className="bg-white border rounded-lg overflow-hidden" data-testid="mw-tracker-history">
          <div className="bg-gray-800 text-white px-4 py-3">
            <h4 className="font-bold">Hit Tracker History (last 50)</h4>
          </div>
          <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-100 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-center text-xs font-bold">Draw</th>
                  <th className="px-3 py-2 text-center text-xs font-bold">Status</th>
                  <th className="px-3 py-2 text-center text-xs font-bold">Actual</th>
                  <th className="px-3 py-2 text-center text-xs font-bold">Per-Ball Predicted / Δ</th>
                  <th className="px-3 py-2 text-center text-xs font-bold">Combo Overlap</th>
                </tr>
              </thead>
              <tbody>
                {tracker.items.map(it => (
                  <tr key={it.target_draw_number} className="border-b">
                    <td className="px-3 py-2 text-center font-mono font-bold">#{it.target_draw_number}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`text-[10px] px-2 py-0.5 rounded ${it.status === 'scored' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>{it.status}</span>
                    </td>
                    <td className="px-3 py-2 text-center font-mono text-xs">{(it.actual || []).join(', ')}</td>
                    <td className="px-3 py-2 text-center font-mono text-xs text-gray-600">
                      {(it.per_ball || []).map(p => `${p.predicted ?? '-'}/${p.delta ?? '-'}`).join('  ')}
                    </td>
                    <td className="px-3 py-2 text-center font-mono text-xs">{it.best_combo_overlap ?? '-'}/5</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-gray-50 rounded p-2">
      <div className="text-[10px] text-gray-500 font-bold uppercase">{label}</div>
      <div className="text-lg font-bold font-mono text-[#002FA7]">{value}</div>
    </div>
  );
}
